import { useEffect, useMemo, useRef, useState } from "react"

/** URLs + Markdown-Links in Plaintext zu klickbaren Links. */
function linkifyPlaintext(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>"']+/g
  let lastIndex = 0
  let key = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index))
    if (m[1] && m[2]) {
      nodes.push(
        <a key={key++} href={m[2]} target="_blank" rel="noreferrer" style={{ color: "#4f46e5" }}>
          {m[1]}
        </a>,
      )
    } else {
      const url = m[0]
      const label = url.length > 60 ? url.slice(0, 57) + "…" : url
      nodes.push(
        <a key={key++} href={url} target="_blank" rel="noreferrer" style={{ color: "#4f46e5" }}>
          {label}
        </a>,
      )
    }
    lastIndex = m.index + m[0].length
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
  return nodes
}

/**
 * Outlook-Plaintext-Quirks raeumen. Wenn eine Mail multipart/alternative ist
 * und der Sender Outlook benutzt, enthaelt der text/plain-Part wortwoertlich
 * `[cid:imageXYZ.JPG@cc90ae1b.49b9dc29]` als Platzhalter fuer Inline-Bilder
 * (Logos in Signaturen). Da wir keine CID-Aufloesung auf dem Plain-Pfad
 * haben, wirken die nur stoerend — wir filtern sie heraus.
 *
 * Plus: lange Bindestrich-Trenner (`-----`) auf max 40 Zeichen kuerzen damit
 * der Reflow nicht auf 4 Bildschirmen explodiert.
 */
function cleanupPlaintext(text: string): string {
  return text
    .replace(/\[cid:[^\]]+\]/gi, "")
    .replace(/-{6,}/g, "──────")
    .replace(/_{6,}/g, "──────")
    // Aufeinanderfolgende Leerzeilen reduzieren
    .replace(/\n{4,}/g, "\n\n\n")
}

/**
 * Outlook-Quote-Header-Erkennung. Liefert den Split-Index oder -1. Erkennt
 * typische Muster:
 *   "Am 13.05.2026, 09:35 schrieb Max Mustermann <max@x.de>:"
 *   "Von: ... Gesendet: ... An: ... Betreff: ..." (Outlook-Block)
 *   Eine Zeile die nur aus `>`-prefixed Zeilen besteht — alles ab dort.
 */
function findQuoteStart(text: string): number {
  const patterns = [
    /^Am\s+\d{1,2}\.\d{1,2}\.\d{2,4}.{0,80}schrieb.+?:$/im,
    /^On\s+.{0,80}wrote:$/im,
    /^Von:\s.+$/im, // Outlook-Block-Header
    /^-{2,}\s*Original.{0,40}-{2,}$/im,
    /^-{2,}\s*Urspr.{0,40}-{2,}$/im,
  ]
  let best = -1
  for (const re of patterns) {
    const m = re.exec(text)
    if (m && m.index >= 0 && (best === -1 || m.index < best)) best = m.index
  }
  // `>`-prefixed Block — finde erste Zeile mit `>` am Anfang
  const lines = text.split("\n")
  let idx = 0
  for (const line of lines) {
    if (/^>\s?/.test(line)) {
      if (best === -1 || idx < best) best = idx
      break
    }
    idx += line.length + 1
  }
  return best
}

/**
 * Rendert einen E-Mail-Body sauber:
 * - HTML wird in einen sandboxed iframe gepackt (echtes Newsletter-Rendering mit Bildern/CSS)
 * - Plaintext wird mit Linkify ausgegeben
 * Auto-Height basierend auf Content + ResizeObserver.
 *
 * Quelle der Wahrheit (early): EmailsList.tsx::EmailBody — hier extrahiert,
 * damit EmailDetail-Seite + Review-Newsletter dieselbe Logik nutzen.
 */
export function EmailBody({
  html,
  minHeight = 200,
  background = "#fff",
  emailId,
}: {
  html: string
  minHeight?: number
  background?: string
  /** Noetig, um `cid:`-Inline-Bilder aufzuloesen. Fehlt sie, bleibt der
   *  HTML-Pfad unveraendert — dann sind eingebettete Bilder eben nicht da. */
  emailId?: number
}) {
  const ref = useRef<HTMLIFrameElement | null>(null)
  const rahmenRef = useRef<HTMLDivElement | null>(null)
  // Timer, Beobachter und Bild-Lauscher des letzten iframe-Ladens — siehe onLoad.
  const aufraeumenRef = useRef<(() => void) | null>(null)
  const [height, setHeight] = useState(minHeight)
  // Breite, die der Rahmen dem iframe geben muss, damit dessen Inhalt
  // hineinpasst. 0 = passt ohnehin (Normalfall).
  const [breite, setBreite] = useState(0)

  // HTML-Erkennung per Regex statt Substring-Liste. Eine Liste verfehlt immer
  // irgendeinen Fall — zuletzt Mails, die nur aus <b>…</b> bestehen: kein
  // <div>, kein <p>, also Plain-Pfad, also roher Quelltext auf dem Schirm.
  // Ein beliebiges schliessendes Tag reicht als Beweis.
  // (Aus der frueheren Zweitfassung in EmailsList.tsx uebernommen, bevor die
  //  geloescht wurde — sie war an dieser Stelle die bessere.)
  const isHtml = /<\/[a-z]+>|<(html|body|div|p|br|table|td|tr|span|img|font|style|meta|a\s+href|!doctype)\b/i
    .test((html || "").slice(0, 2000))

  // Plain-Text-Pfad: Outlook-Muell wegfiltern + Quote-Block visuell trennen.
  const plainRendered = useMemo(() => {
    if (isHtml) return null
    const cleaned = cleanupPlaintext(html || "")
    const splitAt = findQuoteStart(cleaned)
    if (splitAt < 0) {
      return (
        <div data-pii style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13.5, lineHeight: 1.6 }}>
          {linkifyPlaintext(cleaned)}
        </div>
      )
    }
    const head = cleaned.slice(0, splitAt).replace(/\s+$/, "")
    let quote = cleaned.slice(splitAt)
    // `>` am Zeilenanfang entfernen — der visuelle Blockquote-Border ersetzt
    // den Prefix optisch
    quote = quote.replace(/^>\s?/gm, "")
    return (
      <div data-pii style={{ fontFamily: "inherit", fontSize: 13.5, lineHeight: 1.6 }}>
        {head && (
          <div style={{ whiteSpace: "pre-wrap", marginBottom: 12 }}>{linkifyPlaintext(head)}</div>
        )}
        <div
          style={{
            whiteSpace: "pre-wrap",
            borderLeft: "3px solid #cbd5e1",
            paddingLeft: 12,
            color: "#475569",
            marginTop: 8,
          }}
        >
          {linkifyPlaintext(quote)}
        </div>
      </div>
    )
  }, [html, isHtml])

  // Hoehe zuruecksetzen, wenn eine andere Mail in dieselbe Komponente kommt.
  // MUSS vor dem fruehen return stehen: sonst laufen auf dem Plain-Pfad drei
  // Hooks und auf dem HTML-Pfad vier, und React wirft beim Wechsel zwischen
  // zwei Mails unterschiedlichen Typs "Rendered more hooks than expected".
  useEffect(() => {
    setHeight(minHeight)
  }, [html, minHeight])

  // Beim Aushängen alles abräumen, was onLoad nachgestartet hat. Sonst feuern
  // die Nachmess-Timer (bis 10 s) in eine ausgehängte Komponente — in vitest
  // nach dem Abbau von jsdom: „window is not defined", der Deploy-Lauf vom
  // 02.11.2026 brach daran ab, obwohl alle Tests grün waren.
  useEffect(() => () => {
    aufraeumenRef.current?.()
    aufraeumenRef.current = null
  }, [])

  if (!isHtml) {
    return plainRendered
  }

  // `cid:`-Referenzen auf den Inline-Bild-Endpoint umschreiben. HTML-Mails
  // binden eingebettete Bilder als <img src="cid:xyz"> ein — im iframe kann
  // der Browser das nicht aufloesen, die Bilder blieben schlicht leer.
  // Betrifft Kundenfotos genauso wie das Logo unserer eigenen Signatur.
  const htmlMitBildern = emailId
    ? html.replace(
        /(<img[^>]*\bsrc\s*=\s*)(["']?)cid:([^"'\s>]+)\2/gi,
        (_treffer, davor, quote, cid) =>
          `${davor}${quote || '"'}/api/v1/emails/${emailId}/inline-image?cid=${encodeURIComponent(
            decodeURIComponent(cid),
          )}${quote || '"'}`,
      )
    : html

  // Bilder auch in der Hoehe begrenzen (#121): Outlook schreibt Signatur-
  // Logos im zitierten Verlauf feste Masse an (`width:15.6cm;height:8.5cm`),
  // das Logo nahm fast die ganze Ansicht ein. `!important`, weil Absender
  // eigene max-height inline setzen; `object-fit`, damit nichts verzerrt.
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>
    html,body{margin:0;padding:0}
    body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
         font-size:13px;line-height:1.6;color:#0f172a;padding:8px 4px;overflow-x:auto}
    img{max-width:100%;height:auto;max-height:240px!important;object-fit:contain;object-position:left top}
    a{color:#4f46e5;overflow-wrap:anywhere;word-break:break-word}
    blockquote{margin:8px 0;padding:4px 12px;border-left:3px solid #e2e8f0;color:#64748b}
  </style></head><body>${htmlMitBildern}</body></html>`

  const onLoad = () => {
    // Neue Mail im selben iframe: die Timer der vorigen sind hinfällig.
    aufraeumenRef.current?.()
    aufraeumenRef.current = null
    try {
      const iframe = ref.current
      if (!iframe || !iframe.contentDocument) return
      const body = iframe.contentDocument.body
      const doc = iframe.contentDocument.documentElement
      // Zu breite Mails einpassen. Viele Absender schreiben feste Pixelbreiten
      // in ihr HTML (Tobit david®: eine Tabelle mit `WIDTH: 1181px`, gemessen
      // 1202px Inhalt in einem 600px breiten Panel). Unser CSS bremst nur
      // Bilder, Tabellen laufen ueber. Der waagerechte Scrollbalken sitzt dann
      // am unteren Rand der GESAMTEN Mail — hunderte Pixel ausserhalb des
      // Sichtbereichs und damit unerreichbar. Es sah abgeschnitten aus
      // (gemeldet 12.08.2026).
      //
      // Zwei Schritte, weil keiner allein reicht:
      //
      // 1. Verkleinern — aber nur bis 0,7. Bewusst `zoom` und nicht
      //    `table{max-width:100%!important}`: erzwungene Umbrueche zerlegen
      //    mehrspaltige Newsletter. Und bewusst ein flacher Deckel: die
      //    Beispielmail braeuchte 0,499, bei 13px Grundschrift waeren das
      //    6px — passt, liest aber niemand.
      // 2. Was dann noch uebersteht, bekommt einen Scrollbalken AM RAHMEN
      //    statt im iframe. Der Rahmen ist so hoch wie das Sichtfenster, sein
      //    Balken also erreichbar.
      const MINDEST_ZOOM = 0.7
      // Das Zuruecksetzen des Zooms aendert die Groesse des body — und genau
      // darauf horcht der ResizeObserver unten. Ohne dieses Gedaechtnis
      // rechnete er sich im Kreis, solange er lauscht.
      let zuletzt = ""
      const einpassen = () => {
        const verfuegbar = rahmenRef.current?.clientWidth || iframe.clientWidth
        if (!verfuegbar) return
        const alterZoom = body.style.zoom
        body.style.zoom = ""
        const gebraucht = Math.max(body.scrollWidth, doc.scrollWidth)
        const passt = gebraucht <= verfuegbar + 4
        const zoom = passt ? 1 : Math.max(MINDEST_ZOOM, verfuegbar / gebraucht)
        const kennung = `${verfuegbar}|${gebraucht}`
        if (kennung === zuletzt) {
          body.style.zoom = alterZoom
          return
        }
        zuletzt = kennung
        body.style.zoom = passt ? "" : String(zoom)
        setBreite(passt ? 0 : Math.ceil(gebraucht * zoom))
      }
      const updateHeight = () => {
        einpassen()
        const h = Math.max(body.scrollHeight, doc.scrollHeight, minHeight)
        setHeight(h + 8)
      }
      updateHeight()
      const timer = [50, 300, 1000].map((ms) => setTimeout(updateHeight, ms))
      let beobachter: (() => void) | null = null
      if (typeof ResizeObserver !== "undefined") {
        // 🔴 Nicht im Rueckruf selbst messen und anpassen (#158): `einpassen`
        // setzt `zoom` am body und die Rahmenbreite — beides veraendert genau
        // die beobachteten Elemente, noch im selben Frame. Der Browser meldet
        // das als „ResizeObserver loop completed with undelivered
        // notifications" — bei Sanitec voll davon die Konsole. Auf den
        // naechsten Frame verschoben, sieht der Beobachter die Aenderung erst
        // in der naechsten Runde — keine Schleife, gleiche Hoehe.
        let rahmen = 0
        const ro = new ResizeObserver(() => {
          if (rahmen) return
          rahmen = requestAnimationFrame(() => {
            rahmen = 0
            updateHeight()
          })
        })
        ro.observe(body)
        ro.observe(doc)
        beobachter = () => {
          ro.disconnect()
          if (rahmen) cancelAnimationFrame(rahmen)
        }
        timer.push(setTimeout(beobachter, 10000))
      }
      const imgs = Array.from(body.querySelectorAll("img"))
        .filter((img) => !(img as HTMLImageElement).complete)
      imgs.forEach((img) => {
        img.addEventListener("load", updateHeight)
        img.addEventListener("error", updateHeight)
      })
      aufraeumenRef.current = () => {
        timer.forEach(clearTimeout)
        beobachter?.()
        imgs.forEach((img) => {
          img.removeEventListener("load", updateHeight)
          img.removeEventListener("error", updateHeight)
        })
      }
    } catch {
      /* noop */
    }
  }

  return (
    <div ref={rahmenRef} data-pii style={{ overflowX: breite ? "auto" : "hidden", maxWidth: "100%" }}>
      <iframe
        ref={ref}
        srcDoc={srcDoc}
        sandbox="allow-same-origin allow-popups"
        onLoad={onLoad}
        style={{
          width: breite ? `${breite}px` : "100%",
          height, border: 0, background, display: "block",
        }}
        title="email-body"
      />
    </div>
  )
}
